import React from "react";

import {
  ClipboardList,
  DocumentText,
} from "../../../components/icons/svg/outline";
import { formatCurrency } from "../../../utils/common/formatting/numbers";
import {
  emptyRfiCardSummary,
  emptyTmCardSummary,
  PROJECT_CARD_RFI_STATUS_LABELS,
} from "../shared/projectTicketSummaries";

/** Text scale for card tiles (``sm``) vs list-view tooltips (``md``). */
const TILE_TEXT_SIZES = {
  sm: {
    title: "text-[11px]",
    body: "text-[10px]",
    iconBox: "h-4 w-4",
    icon: "h-2.5 w-2.5",
  },
  // Match Projects list row ``text-sm`` so hover tiles don't feel scaled down.
  md: {
    title: "text-sm",
    body: "text-sm",
    iconBox: "h-5 w-5",
    icon: "h-3.5 w-3.5",
  },
};

/**
 * One compact status count cell for a ticket summary tile.
 *
 * @param {Object} props
 * @param {string} props.label - Status label.
 * @param {number} props.count - Status count.
 * @param {string} props.bodyClassName - Body text size class.
 * @returns {JSX.Element}
 */
function StatusCount({ label, count, bodyClassName }) {
  return (
    <div className="flex min-w-0 items-baseline justify-between gap-1 leading-none">
      <span className={`truncate font-normal text-gray-500 ${bodyClassName}`}>
        {label}
      </span>
      <span
        className={`flex-shrink-0 font-semibold tabular-nums text-gray-900 ${bodyClassName}`}
      >
        {count}
      </span>
    </div>
  );
}

/**
 * Compact T&M / RFI status summary tile (card body and list tooltips).
 *
 * When ``footer`` is set (T&M), statuses + total fill a 3-column grid with the
 * total in the bottom-right cell (native ``title`` tooltip, no label).
 *
 * @param {Object} props
 * @param {string} props.title - Module title (e.g. ``T&M``, ``RFI``).
 * @param {React.ComponentType<{className?: string}>} props.Icon - Leading icon.
 * @param {string} props.toneClassName - Icon chip color classes.
 * @param {Array<{ label: string, count: number }>} props.rows - Status rows.
 * @param {string} [props.footer] - Optional total-value cell (bottom-right).
 * @param {boolean} [props.isLoading=false] - Whether ticket data is still loading.
 * @param {"sm"|"md"} [props.size="sm"] - Text scale (``md`` for list tooltips).
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export const CompactTicketSummary = ({
  title,
  Icon,
  toneClassName,
  rows,
  footer,
  isLoading = false,
  size = "sm",
  className = "",
}) => {
  const textSize = TILE_TEXT_SIZES[size] || TILE_TEXT_SIZES.sm;
  // T&M uses 3 cols (5 statuses + total); RFI stays 2×2.
  const gridClassName = footer ? "grid-cols-3" : "grid-cols-2";

  return (
    <div
      className={`min-w-0 rounded-md border border-gray-200/80 bg-gradient-to-br from-gray-50/90 via-white to-white px-1.5 py-1 ${className}`}
    >
      <div className="mb-0.5 flex items-center gap-1">
        <div
          className={`flex flex-shrink-0 items-center justify-center rounded ${textSize.iconBox} ${toneClassName}`}
        >
          <Icon className={textSize.icon} aria-hidden />
        </div>
        <div
          className={`truncate font-semibold leading-none tracking-tight text-gray-900 ${textSize.title}`}
        >
          {title}
        </div>
      </div>
      {isLoading ? (
        <div className={`font-normal text-gray-400 ${textSize.body}`}>
          Loading…
        </div>
      ) : (
        <div className={`grid ${gridClassName} gap-x-1.5 gap-y-0.5`}>
          {rows.map((row) => (
            <StatusCount
              key={row.label}
              label={row.label}
              count={row.count}
              bodyClassName={textSize.body}
            />
          ))}
          {footer ? (
            <div
              title="Total Value"
              className={`min-w-0 truncate text-right font-semibold tabular-nums leading-none text-emerald-700 ${textSize.body}`}
            >
              {footer}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

/**
 * Format a T&M total-value for list cells and tile footers.
 *
 * @param {number} totalValue - Sum of priced report totals.
 * @returns {string}
 */
export function formatTmTotalValue(totalValue) {
  return totalValue > 0 ? formatCurrency(totalValue) : "$0.00";
}

/**
 * Standard T&M summary tile used on project cards and list tooltips.
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact T&M status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {"sm"|"md"} [props.size="sm"] - Text scale (``md`` for list tooltips).
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export function ProjectTmSummaryTile({
  summary = emptyTmCardSummary(),
  isLoading = false,
  size = "sm",
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="T&M"
      Icon={ClipboardList}
      toneClassName="bg-emerald-50 text-emerald-700"
      rows={[
        { label: "Created", count: summary.open },
        { label: "Sent", count: summary.sent },
        { label: "Signed", count: summary.signed },
        { label: "Priced", count: summary.priced },
        { label: "Submitted", count: summary.submitted },
      ]}
      footer={formatTmTotalValue(summary.totalValue)}
      isLoading={isLoading}
      size={size}
      className={className}
    />
  );
}

/**
 * Standard RFI summary tile used on project cards and list tooltips.
 *
 * @param {Object} props
 * @param {Object} [props.summary] - Compact RFI status summary.
 * @param {boolean} [props.isLoading=false] - Whether ticket lists are still loading.
 * @param {"sm"|"md"} [props.size="sm"] - Text scale (``md`` for list tooltips).
 * @param {string} [props.className=""] - Optional outer class overrides.
 * @returns {JSX.Element}
 */
export function ProjectRfiSummaryTile({
  summary = emptyRfiCardSummary(),
  isLoading = false,
  size = "sm",
  className = "",
}) {
  return (
    <CompactTicketSummary
      title="RFI"
      Icon={DocumentText}
      toneClassName="bg-sky-50 text-sky-700"
      rows={[
        { label: PROJECT_CARD_RFI_STATUS_LABELS.open, count: summary.open },
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.reviewing,
          count: summary.reviewing,
        },
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.responding,
          count: summary.responding,
        },
        {
          label: PROJECT_CARD_RFI_STATUS_LABELS.closed,
          count: summary.closed,
        },
      ]}
      isLoading={isLoading}
      size={size}
      className={className}
    />
  );
}

export default CompactTicketSummary;
